import { I18NText, Position, State, StateObj } from 'src/types'
import { positionChecker } from 'src/logic'
import { seriesChecker } from 'src/features/atoms/glove/simulationSpecification'
import { checkAtomsWeb2 } from 'src/features/atoms/glove/logic'
import { SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE } from '../constants/colors'
import { APL_UR002_PLUS_PRODUCT_NUMBER } from '../constants/baseModel'

export const colorSelectObjects = (tannerValue: string, partsKey: string, isStaffOnly: boolean, t: I18NText): StateObj[] => {
  if (isStaffOnly) return t.DISPATCH_COLOR_OBJECTS[partsKey]
  switch (tannerValue) {
    case 'europeanSteer':
      return t.DISPATCH_EUROPEAN_LEATHER_COLOR_OBJECTS[partsKey]
    case 'northSteer':
      return t.DISPATCH_NORTH_LEATHER_COLOR_OBJECTS[partsKey]
    case 'steer':
      return t.DISPATCH_STEER_LEATHER_COLOR_OBJECTS[partsKey]
  }
}

export const useSelectPartsObject = (figurePanelNum: number, position: Position, state: State, t: I18NText): StateObj[] => {
  const existFingerCover = state.fingerGuardType.value !== 'none'
  const frontFigurePanel = 0
  const printLeatherTypeValue = state.printLeatherType?.value
  const isPrintLeatherTypeSelected = printLeatherTypeValue && printLeatherTypeValue !== 'unselected' && printLeatherTypeValue !== 'none'

  const normalParts = figurePanelNum === frontFigurePanel ? selectFrontPartsObject(position, t) : selectBackPartsObject(position, state, t)
  const partsWithPrintLeatherTypeSelected = normalParts?.filter((p) => SELECTABLE_PARTS_LIST_WITH_PRINT_LEATHER_TYPE.includes(p.value))
  const { isCatcher, isPitcher } = positionChecker(position)

  if (isPrintLeatherTypeSelected) {
    if (isCatcher && state.backStyle.value === 'regularBackStyleCatcher') {
      return [...partsWithPrintLeatherTypeSelected, { label: 'バンド', value: 'listBelt', order: 14 }]
    }

    if (isPitcher && state.backStyle.value === 'firstBackStyle') {
      return [...partsWithPrintLeatherTypeSelected, { label: 'バンド', value: 'listBelt', order: 14 }]
    }

    return partsWithPrintLeatherTypeSelected
  }

  const filteredFingerCoverPartsObjects = existFingerCover ? normalParts : normalParts.filter((x) => x.value !== 'indexCover')
  const filteredJointCheckerWebPartsObjects = checkAtomsWeb2(state.webParts?.value, position)
    ? [...filteredFingerCoverPartsObjects, t.WEB2_PARTS_OBJECT].sort((a, b) => a.order - b.order)
    : filteredFingerCoverPartsObjects

  return filteredJointCheckerWebPartsObjects
}

const selectFrontPartsObject = (specifiedPosition: Position, t: I18NText) => {
  if (specifiedPosition === 'catcher') {
    return t.CATCH_FACE_FOR_CATCHER_OBJECTS
  } else {
    return t.CATCH_FACE_OBJECTS
  }
}

// private
const selectBackPartsObject = (specifiedPosition: Position, state: State, t: I18NText) => {
  const { is2025Agl } = seriesChecker(state.baseModel.productNumber)
  switch (specifiedPosition) {
    case 'pitcher':
      switch (state?.backStyle?.value) {
        case 'firstBackStyle':
          return t.FIRST_BACK_OBJECTS
        case 'crownBackStyle':
          return t.CROWN_BACK_OBJECTS
        default:
          return t.BELT_BACK_OBJECTS
      }
    case 'infielder':
      if (is2025Agl) {
        if (state?.baseModel?.isUBack) {
          return t.UBACK_OBJECTS
        } else {
          return t.NO_BELT_BACK_OBJECTS
        }
      } else {
        if (state?.baseModel?.isUBack) {
          return t.UBACK_OBJECTS
        } else {
          return t.BELT_BACK_OBJECTS // ベルトパーツあり
        }
      }
    case 'outfielder':
      if (is2025Agl) {
        return t.NO_BELT_BACK_OBJECTS
      } else {
        return t.BELT_BACK_OBJECTS
      }
    case 'catcher':
      switch (state.baseModel.productNumber === APL_UR002_PLUS_PRODUCT_NUMBER ? state.baseModel.backStyle : state.backStyle.value) {
        case 'regularBackStyleCatcher':
        case 'regularBackStyle':
          return t.C_MITT_REGULAR_BACK_OBJECTS
        case 'beltBackStyleCatcher':
        case 'beltBackStyle':
          return t.C_MITT_BELT_BACK_OBJECTS
        default:
          return t.C_MITT_BACK_OBJECTS
      }
    case 'first':
      if (is2025Agl) {
        return t.F_MITT_NO_BELT_BACK_OBJECTS
      } else {
        return t.F_MITT_BACK_OBJECTS
      }
  }
}

export const useColorObjects = (specifiedPosition: Position, state: State) => {
  const baseModelBackStyle = state?.baseModel?.backStyle
  const {
    all,
    catchFace,
    lace,
    web,
    web2,
    edge,
    stitch,
    hamidashi,
    thumbHook,
    littleHook,
    indexCover,
    linings,
    mouton,
    atomsLabel,
    catchFaceTarget,
    fingerStand,
    indexFingerTipOut,
    thumbIndexMiddleRight, // 親指・人差指・中指1
    listBelt,
    middleLeftRing, // 中指2・薬指
    littleOut, // 小指左
    middleLeftRingLittle, // 中指2・薬指・小指
    thumbWebAndIndexWeb, // UBackで使用
    thumb,
    underWeb,
    boomerang
  } = state
  const commonParts = {
    all,
    catchFace,
    lace,
    web,
    web2,
    edge,
    stitch,
    hamidashi,
    thumbHook,
    littleHook,
    indexCover,
    linings,
    mouton,
    atomsLabel
  }
  const catcherMittCommonParts = {
    catchFaceTarget,
    fingerStand,
    thumbIndexMiddleRight // 親指・人差指・中指1
  }
  switch (specifiedPosition) {
    case 'catcher':
      switch (baseModelBackStyle) {
        case 'regularBackStyleCatcher':
          return {
            ...commonParts,
            ...catcherMittCommonParts,
            listBelt,
            middleLeftRing, // 中指2・薬指
            littleOut, // 小指左
            indexFingerTipOut
          }

        case 'beltBackStyleCatcher':
          return {
            ...commonParts,
            ...catcherMittCommonParts,
            middleLeftRingLittle // 中指2・薬指・小指
          }
        default:
          return {}
      }
    case 'first':
      return {
        ...commonParts,
        listBelt,
        thumb,
        underWeb,
        boomerang
      }
    default:
      // 投手・内野手・外野手
      return {
        ...commonParts,
        listBelt, // 必要＿王冠・ベルト・ファーストバック
        thumbOut: state?.thumbOut, // 必要＿王冠・ベルト・ファーストバック
        thumbWeb: state?.thumbWeb, // 必要＿王冠・ベルト・
        thumbIndexMiddle: state?.thumbIndexMiddle, // 必要＿ファーストバック
        indexWeb: state?.indexWeb, // 必要＿王冠・ベルト
        indexLeftMiddleRight: state?.indexLeftMiddleRight, // 必要＿王冠
        middleLeftRingRight: state?.middleLeftRingRight, // 必要＿王冠・ファーストバック
        ringLeftLittleRight: state?.ringLeftLittleRight, // 必要＿王冠・ファーストバック
        indexMiddle: state?.indexMiddle, // 必要＿ベルト
        middleIndex: state?.middleIndex, // 必要＿ベルト
        middleRing: state?.middleRing, // 必要＿ベルト
        ringMiddle: state?.ringMiddle, // 必要＿ベルト
        ringLittle: state?.ringLittle, // 必要＿ベルト
        littleRing: state?.littleRing, // 必要＿ベルト
        littleOut, // 必要＿王冠・ベルト・ファーストバック
        thumbWebAndIndexWeb // UBackで必要
      }
  }
}

export const hamidashiObjectSwitcher = (position: Position, t: I18NText): StateObj[] => {
  const { isCatcher } = positionChecker(position)
  return isCatcher ? t.HAMIDASHI_COLOR_FOR_CATCHER_OBJECTS : t.HAMIDASHI_COLOR_OBJECTS
}

export const colorCautionGenerator = (partsKey: string, t: I18NText): string => {
  switch (partsKey) {
    case 'lace':
      return t.CAUTION_LACE
  }
}
